import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, AlertTriangle, CheckCircle, User, Mic, Upload, X } from 'lucide-react';
import './WorkflowGeneration.css';

const WorkflowGeneration = ({
  data,
  onChange,
  onGenerate,
  isGenerating,
  isButtonDisabled,
  selectedAvatar,
  selectedVoice,
}) => {
  const navigate = useNavigate();

  const [avatarMode, setAvatarMode] = useState(
    data.digitalHuman?.virtualmanKey ? 'key' : 'select'
  );
  const [voiceMode, setVoiceMode] = useState(
    data.voice?.audioUrl ? 'audio' : 'select'
  );
  const [materialsMode, setMaterialsMode] = useState('url');
  const [bgmMode, setBgmMode] = useState('library');
  const [selectedBgm, setSelectedBgm] = useState(null);

  // Video upload
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Audio upload
  const [bgmDragOver, setBgmDragOver] = useState(false);
  const [uploadedBgm, setUploadedBgm] = useState(null);
  const [bgmUploadError, setBgmUploadError] = useState('');
  const bgmFileInputRef = useRef(null);

  const BACKGROUND_MUSIC = [
    { id: '1', title: '轻松愉快的背景音乐', duration: '05:12' },
    { id: '2', title: '商务正式背景音乐',   duration: '03:45' },
    { id: '3', title: '温馨浪漫背景音乐',   duration: '04:28' },
    { id: '4', title: '动感节奏背景音乐',   duration: '06:15' },
    { id: '5', title: '安静舒缓背景音乐',   duration: '03:52' },
    { id: '6', title: '科技感电子音乐',     duration: '07:30' },
  ];

  const ACCEPTED_VIDEO = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
  const MAX_VIDEO = 30 * 1024 * 1024;
  const ACCEPTED_AUDIO = ['audio/mpeg', 'audio/wav', 'audio/aac'];
  const MAX_AUDIO = 50 * 1024 * 1024;

  const addFiles = (fileList) => {
    const next = [...uploadedFiles];
    Array.from(fileList).forEach(f => {
      if (!ACCEPTED_VIDEO.includes(f.type)) return;
      if (f.size > MAX_VIDEO) return;
      if (next.find(x => x.name === f.name && x.size === f.size)) return;
      next.push(f);
    });
    setUploadedFiles(next);
  };

  const removeFile = (idx) => setUploadedFiles(prev => prev.filter((_, i) => i !== idx));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleBgmFile = (file) => {
    if (!file) { setUploadedBgm(null); setBgmUploadError(''); return; }
    if (!ACCEPTED_AUDIO.includes(file.type)) {
      setBgmUploadError('不支持的格式，请上传 MP3、WAV 或 AAC 文件');
      return;
    }
    if (file.size > MAX_AUDIO) {
      setBgmUploadError('文件大小超过 50MB 限制');
      return;
    }
    setUploadedBgm(file);
    setBgmUploadError('');
  };

  const handleBgmDrop = (e) => {
    e.preventDefault();
    setBgmDragOver(false);
    if (e.dataTransfer.files[0]) handleBgmFile(e.dataTransfer.files[0]);
  };

  const set = (section, field, value) => onChange(section, { [field]: value });

  // ── Validation ──────────────────────────────────────────────────
  const virtualmanKey = data.digitalHuman?.virtualmanKey || '';
  const step1Done =
    avatarMode === 'select'
      ? !!(data.digitalHuman?.selectedHuman)
      : virtualmanKey.trim().length > 0;

  const audioUrl = data.voice?.audioUrl || '';
  const isValidAudioUrl = /^https?:\/\/.+/.test(audioUrl.trim());
  const step2Done =
    voiceMode === 'select'
      ? !!(data.voice?.selectedVoice && data.voice?.script?.trim())
      : isValidAudioUrl;

  const urls = data.materials?.urls || [];
  const step3Done = materialsMode === 'url' ? urls.length > 0 : uploadedFiles.length > 0;

  const bgmUrl = data.bgMusic?.audioUrl || '';
  const step4Done =
    bgmMode === 'library' ? !!selectedBgm :
    bgmMode === 'url'     ? bgmUrl.trim().length > 0 :
    bgmMode === 'upload'  ? !!uploadedBgm : false;

  const isValid = step1Done && step2Done && step3Done && step4Done;

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="wf-page">

      {/* ── Step 1: 数字人 ── */}
      <div className="wf-step-card">
        <div className="wf-step-header">
          <span className={`wf-step-num ${step1Done ? 'done' : ''}`}>1</span>
          <div>
            <p className="wf-step-title">选择数字人</p>
          </div>
          {step1Done && <CheckCircle size={16} className="wf-check" />}
        </div>

        <div className="wf-seg">
          <button
            className={`wf-seg-btn ${avatarMode === 'select' ? 'active' : ''}`}
            onClick={() => setAvatarMode('select')}
          >
            选择已创建的数字人
          </button>
          <button
            className={`wf-seg-btn ${avatarMode === 'key' ? 'active' : ''}`}
            onClick={() => setAvatarMode('key')}
          >
            输入数字人 VirtualmanKey
          </button>
        </div>

        {avatarMode === 'select' ? (
          <div className="wf-select-area">
            {selectedAvatar ? (
              <div className="wf-selected-item">
                {selectedAvatar.thumbnail && (
                  <img src={selectedAvatar.thumbnail} alt={selectedAvatar.name} className="wf-selected-thumb" />
                )}
                <div className="wf-selected-info">
                  <p className="wf-selected-name">{selectedAvatar.name}</p>
                  <p className="wf-selected-key">{selectedAvatar.key?.substring(0, 20)}...</p>
                </div>
                <button
                  className="wf-change-btn"
                  onClick={() => navigate('/my-avatars', { state: { from: '/generate-video', fromTab: 'workflow' } })}
                >
                  <User size={13} /> 更换
                </button>
              </div>
            ) : (
              <button
                className="wf-pick-btn"
                onClick={() => navigate('/my-avatars', { state: { from: '/generate-video', fromTab: 'workflow' } })}
              >
                <User size={14} /> 从数字人库中选择
              </button>
            )}
          </div>
        ) : (
          <div className="wf-input-area">
            <input
              className="wf-input"
              placeholder="请输入数字人 VirtualmanKey"
              value={virtualmanKey}
              onChange={e => set('digitalHuman', 'virtualmanKey', e.target.value)}
            />
          </div>
        )}
      </div>

      {/* ── Step 2: 声音 ── */}
      <div className="wf-step-card">
        <div className="wf-step-header">
          <span className={`wf-step-num ${step2Done ? 'done' : ''}`}>2</span>
          <div>
            <p className="wf-step-title">选择声音</p>
          </div>
          {step2Done && <CheckCircle size={16} className="wf-check" />}
        </div>

        <div className="wf-seg">
          <button
            className={`wf-seg-btn ${voiceMode === 'select' ? 'active' : ''}`}
            onClick={() => setVoiceMode('select')}
          >
            选择已创建的克隆声音
          </button>
          <button
            className={`wf-seg-btn ${voiceMode === 'audio' ? 'active' : ''}`}
            onClick={() => setVoiceMode('audio')}
          >
            输入音频链接
          </button>
        </div>

        {voiceMode === 'select' ? (
          <div className="wf-select-area">
            {selectedVoice ? (
              <div className="wf-selected-item">
                <Mic size={20} className="wf-selected-icon" />
                <div className="wf-selected-info">
                  <p className="wf-selected-name">{selectedVoice.name}</p>
                  <p className="wf-selected-key">{selectedVoice.key?.substring(0, 20)}...</p>
                </div>
                <button
                  className="wf-change-btn"
                  onClick={() => navigate('/voice-cloning', { state: { from: '/generate-video', fromTab: 'workflow' } })}
                >
                  <Mic size={13} /> 更换
                </button>
              </div>
            ) : (
              <button
                className="wf-pick-btn"
                onClick={() => navigate('/voice-cloning', { state: { from: '/generate-video', fromTab: 'workflow' } })}
              >
                <Mic size={14} /> 从声音库中选择
              </button>
            )}
            <div className="wf-form-group" style={{ marginTop: 12 }}>
              <label className="wf-label">播报文案 <span className="wf-required">*</span></label>
              <textarea
                className="wf-textarea"
                rows={4}
                placeholder="输入让数字人说的文案..."
                value={data.voice?.script || ''}
                onChange={e => set('voice', 'script', e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="wf-input-area">
            <input
              className={`wf-input ${audioUrl && !isValidAudioUrl ? 'error' : ''}`}
              placeholder="请输入音频链接"
              value={audioUrl}
              onChange={e => set('voice', 'audioUrl', e.target.value)}
            />
          </div>
        )}
      </div>

      {/* ── Step 3: 素材视频 ── */}
      <div className="wf-step-card">
        <div className="wf-step-header">
          <span className={`wf-step-num ${step3Done ? 'done' : ''}`}>3</span>
          <div>
            <p className="wf-step-title">选择素材视频</p>
            <p className="wf-step-desc">混剪时，AI会自动从你提供的素材视频中选取多段内容插入到成片中</p>
          </div>
          {step3Done && <CheckCircle size={16} className="wf-check" />}
        </div>

        <div className="wf-seg">
          <button
            className={`wf-seg-btn ${materialsMode === 'upload' ? 'active' : ''}`}
            onClick={() => setMaterialsMode('upload')}
          >
            上传视频文件
          </button>
          <button
            className={`wf-seg-btn ${materialsMode === 'url' ? 'active' : ''}`}
            onClick={() => setMaterialsMode('url')}
          >
            输入视频 url 地址
          </button>
        </div>

        {materialsMode === 'upload' ? (
          <div className="wf-input-area">
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/x-msvideo"
              multiple
              style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <div
              className={`wf-dropzone ${dragOver ? 'dragover' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <div className="wf-dropzone-icon">
                <Upload size={28} />
              </div>
              <p className="wf-dropzone-text">点击或拖拽到此处上传视频</p>
            </div>
            {uploadedFiles.length > 0 && (
              <ul className="wf-file-list">
                {uploadedFiles.map((f, i) => (
                  <li key={i} className="wf-file-item">
                    <span className="wf-file-name">{f.name}</span>
                    <span className="wf-file-size">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                    <button className="wf-file-remove" onClick={() => removeFile(i)}>
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="wf-format-tip">
              要求 MP4、mov、avi 格式，单个素材文件不能超过 30MB，<br />可上传多个素材
            </div>
          </div>
        ) : (
          <div className="wf-input-area">
            <textarea
              className="wf-textarea"
              rows={5}
              placeholder="请输入视频URL地址，每行一个链接"
              value={urls.join('\n')}
              onChange={e =>
                onChange('materials', {
                  urls: e.target.value.split('\n').map(s => s.trim()).filter(Boolean),
                  type: 'url',
                })
              }
            />
            <p className="wf-hint">每行一个视频链接，将自动去除空行</p>
          </div>
        )}
      </div>

      {/* ── Step 4: 背景音乐 ── */}
      <div className="wf-step-card">
        <div className="wf-step-header">
          <span className={`wf-step-num ${step4Done ? 'done' : ''}`}>4</span>
          <div>
            <p className="wf-step-title">选择背景音乐</p>
            <p className="wf-step-desc">BGM 的时长要超过最终的成片时长，BGM 过短时不会自动循环</p>
          </div>
          {step4Done && <CheckCircle size={16} className="wf-check" />}
        </div>

        <div className="wf-seg">
          <button
            className={`wf-seg-btn ${bgmMode === 'library' ? 'active' : ''}`}
            onClick={() => setBgmMode('library')}
          >
            从音频库中选择
          </button>
          <button
            className={`wf-seg-btn ${bgmMode === 'url' ? 'active' : ''}`}
            onClick={() => setBgmMode('url')}
          >
            输入音频 url 地址
          </button>
          <button
            className={`wf-seg-btn ${bgmMode === 'upload' ? 'active' : ''}`}
            onClick={() => setBgmMode('upload')}
          >
            上传音频文件
          </button>
        </div>

        {bgmMode === 'library' && (
          <div className="wf-music-list">
            {BACKGROUND_MUSIC.map(m => (
              <div
                key={m.id}
                className={`wf-music-item ${selectedBgm === m.id ? 'selected' : ''}`}
                onClick={() => setSelectedBgm(m.id)}
              >
                <span className="wf-music-play">
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><path d="M0 0v8l6-4z"/></svg>
                </span>
                <span className="wf-music-title">{m.title}</span>
                <span className="wf-music-duration">{m.duration}</span>
              </div>
            ))}
          </div>
        )}
        {bgmMode === 'url' && (
          <div className="wf-input-area">
            <input
              className="wf-input"
              placeholder="请输入音频URL地址"
              value={bgmUrl}
              onChange={e => set('bgMusic', 'audioUrl', e.target.value)}
            />
          </div>
        )}
        {bgmMode === 'upload' && (
          <div className="wf-input-area">
            <input
              ref={bgmFileInputRef}
              type="file"
              accept=".mp3,.wav,.aac"
              style={{ display: 'none' }}
              onChange={e => { handleBgmFile(e.target.files[0]); e.target.value = ''; }}
            />
            <div
              className={`wf-dropzone ${bgmDragOver ? 'dragover' : ''}`}
              onClick={() => bgmFileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setBgmDragOver(true); }}
              onDragLeave={() => setBgmDragOver(false)}
              onDrop={handleBgmDrop}
            >
              <div className="wf-dropzone-icon"><Upload size={28} /></div>
              <p className="wf-dropzone-text">点击上传音频文件</p>
            </div>
            {uploadedBgm && (
              <div className="wf-file-list">
                <div className="wf-file-item">
                  <span className="wf-file-name">{uploadedBgm.name}</span>
                  <span className="wf-file-size">{(uploadedBgm.size / 1024 / 1024).toFixed(1)} MB</span>
                  <button className="wf-file-remove" onClick={() => handleBgmFile(null)}>
                    <X size={12} />
                  </button>
                </div>
              </div>
            )}
            {bgmUploadError && <p className="wf-error">{bgmUploadError}</p>}
            <div className="wf-format-tip">支持MP3、WAV、AAC格式，文件大小不超过 50MB</div>
          </div>
        )}
      </div>

      {/* ── Generate ── */}
      <div className="wf-generate">
        <button
          className="wf-generate-btn"
          onClick={onGenerate}
          disabled={isButtonDisabled}
        >
          {isGenerating
            ? <><div className="wf-spinner" />提交中，请稍候...</>
            : <><Video size={15} />开始生成混剪视频</>
          }
        </button>
        <div className="wf-status">
          {!isValid ? (
            <span className="wf-status--warn">
              <AlertTriangle size={13} />请完成以上所有步骤后开始生成
            </span>
          ) : (
            <span className="wf-status--ok">
              <CheckCircle size={13} />所有设置已完成，可以开始生成
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowGeneration;
